import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";

const features = [
  {
    title: 'Effortless Event Coordination',
    description: `Manage all events in one place, stay on top of key details, and enable self-booking through an interactive booking page. Our platform streamlines the entire coordination process with one-click event approvals and real-time updates.`,
    image: '/images/solutions/corporate-volunteering/event-coordination.jpg',
    bullets: [
      'Manage All Events in One Place – Accept, reschedule, or modify events with a single click.',
      'Stay on Top of Key Details – Get real-time updates on event timelines, participant status, and logistics.',
      'Interactive Booking Page – Corporates can self-book available events, reducing back-and-forth coordination.',
    ]
  },
  {
    title: 'Smart Event Notifications & Updates',
    description: `Say goodbye to email chaos! Our centralized communication system ensures no update goes unnoticed. Track participation, manage notifications, and keep everyone in the loop,all from a single dashboard.`,
    image: '/images/solutions/corporate-volunteering/smart-notifications.jpg',
    bullets: [
      'Centralized Communication Hub - Goodbye, Email Chaos! No more missed updates or endless email chains,manage all event communications in a single dashboard.',
      'Automated Event Updates - Get real-time notifications for schedule changes, confirmations, and reminders.',
      'Custom Event Udpates - Send custom notifications to participants and event bookers for each stage from a single place.',
      'Seamless Document & Resource Sharing – Upload and share event-related materials like volunteer guides, waivers, presentation decks, and links,all accessible in one place.',
      
    ]
  },
  {
    title: 'Smarter Volunteer Attendance & Impact Tracking',
    description: `Managing volunteers shouldn’t be a logistical challenge. CauseConnect makes it effortless to track registrations, attendance, and participation while ensuring seamless communication before and after the event.`,
    image: '/images/solutions/corporate-volunteering/event-logistics.jpg',
    bullets: [
      'Smooth Registration & Participation Management – Track sign-ups in real time and manage last-minute dropouts efficiently.',
      'Bulk Upload & On-the-Spot Attendance – Quickly update attendance records and instantly reflect participation data in event reports.',
      'Automated Volunteer Communications – Send pre-event guides, real-time reminders, and post-event impact reports effortlessly.',
      
    ]
  },
  {
    title: 'Automated Post-Event Follow-Ups & Surveys',
    description: `Maximize event impact with our automated follow-up system. Collect feedback, generate reports, and recognize volunteers,all automatically handled by our platform.`,
    image: '/images/solutions/corporate-volunteering/post-event-followup.jpg',
    bullets: [
      'Automated Feedback Collection – Get structured feedback from volunteers and corporate partners effortlessly. Feedback Collection',
      'AI-Powered Impact Reports – Our AI Growth Engine captures key event insights, providing detailed reports with minimal effort.AI-Powered Impact Reports',
      'Certificate & Recognition Tools – Reward participants for their contributions and recognize their impact.',
      
    ]
  },
];

export const CorporateVolunteeringPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-3xl font-bold mb-5">
              CauseConnect: The Ultimate Corporate Volunteering Solution For Your Nonprofit
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-6">
              Streamline, Manage, and Elevate Your Volunteering Events. All in One Place
            </p>
            <p className="text-lg text-gray-200">
              Corporate volunteering should be simple, impactful, and scalable. With CauseConnect, nonprofits can efficiently organize, track, and optimize their volunteering events, without the hassle of endless email threads, manual follow-ups, and logistical roadblocks.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        {features.map((feature, index) => (
          <div 
            key={feature.title}
            className={`flex flex-col md:flex-row items-center gap-12 py-16 ${
              index % 2 === 1 ? 'md:flex-row-reverse' : ''
            }`}
          >
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{feature.title}</h2>
              <p className="text-gray-600 mb-6">{feature.description}</p>
              <ul className="space-y-3">
                {feature.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1">
              <img
                src={feature.image}
                alt={feature.title}
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-[#2D7D46] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to take corporate volunteering to the next level?
          </h2>
          <p className="text-xl mb-8">
            Sign up today and experience the future of effortless, high-impact volunteering!
          </p>
          <Button
            size="lg"
            className="bg-white text-[#2D7D46] hover:bg-gray-100"
            onClick={() => navigate('/signup')}
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CorporateVolunteeringPage;
